import {
  StudiestedApiContext,
  StudiestedApiStore,
} from './StudiestedApiContext';
import {
  PraksisstedApiContext,
  PraksisstedApiStore,
} from './PraksisstedApiContext';
import { StudentApiContext, StudentApiStore } from './StudentApiContext';
import { RoletypeDto } from './generated/login-generated-api';
import { AdminApiContext, AdminApiStore } from './AdminApiContext';
import { useContext } from 'react';
import { SelectedRoleContext } from '../providers';

function useApi<T extends RoletypeDto>(
  roleType: T,
): T extends typeof RoletypeDto.Studiested
  ? StudiestedApiStore
  : T extends typeof RoletypeDto.Praksissted
  ? PraksisstedApiStore
  : T extends typeof RoletypeDto.Student
  ? StudentApiStore
  : AdminApiStore;

function useApi<T extends RoletypeDto>(
  roleType: T,
): StudiestedApiStore | PraksisstedApiStore | StudentApiStore | AdminApiStore {
  const studiestedApiContext = useContext(StudiestedApiContext);
  const praksisstedApiContext = useContext(PraksisstedApiContext);
  const studentApiContext = useContext(StudentApiContext);
  const adminApiContext = useContext(AdminApiContext);

  switch (roleType) {
    case RoletypeDto.Studiested:
      return studiestedApiContext;
    case RoletypeDto.Praksissted:
      return praksisstedApiContext;
    case RoletypeDto.Student:
      return studentApiContext;
    default:
      return adminApiContext;
  }
}

/* Must be used within SelectedRoleContext, i.e. further down the application hierarchy than app.tsx */
export const useStudiestedOrPraksisstedApi = ():
  | StudiestedApiStore
  | PraksisstedApiStore => {
  const { type } = useContext(SelectedRoleContext)!;

  if (type !== RoletypeDto.Studiested && type !== RoletypeDto.Praksissted) {
    throw Error('The current user role cannot access specified apis');
  }

  return useApi(type);
};

export default useApi;
